import { PageController } from '@client/components/_core/layout/PageController';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { PUBLIC_READINESS_QUERY_META } from '@client/trpc/query-meta';
import { startRouteChunkPreloads } from '@client/utils/route-preloader';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

// Legacy public complaint link. Resolve the key in `beforeLoad`, which is not
// code-split, so the lookup starts as soon as the router matches instead of
// after the lazy layout/component chunks download and the component mounts
// (CUR-1599). A failed lookup falls through to the component, which keeps its
// own query and default retry policy and renders the failure inline. The
// complaint form's route chunks start loading before the lookup, so their
// download and evaluation overlap its round trip instead of waiting for the
// redirect to match those routes (CUR-1599 follow-up, CUR-2184).
export const Route = createFileRoute('/public/new-complaint-by-key/$key')({
    beforeLoad: async ({ context, params, search }): Promise<void> => {
        startRouteChunkPreloads(['/public', '/public/$orgId', '/public/$orgId/complaint/new']);
        const orgId = await context.trpcUtils.org.getOrgIdByKey
            .fetch({ key: params.key }, { retry: false, meta: PUBLIC_READINESS_QUERY_META })
            .catch(() => undefined);
        if (orgId === undefined) {
            return;
        }
        // Thrown only after the lookup settles, so no catch can swallow the router's redirect.
        throw redirect({ to: '/public/$orgId/complaint/new', params: { orgId }, search, replace: true });
    },
    component: NewComplaintByKey,
});

function NewComplaintByKey() {
    const { key } = Route.useParams();
    const searchParams = Route.useSearch();
    const { data, error, refetch, isFetching, isPaused } = trpc.org.getOrgIdByKey.useQuery({ key });
    const navigate = useNavigate();

    useEffect(() => {
        if (!data) {
            return;
        }

        navigate({ to: '/public/$orgId/complaint/new', params: { orgId: data }, search: searchParams, replace: true });
    }, [data]);

    // A refetch of a query without data resets `error` to null while it runs, so keep the last
    // failure on screen until the retry settles: the panel then shows its retry in progress
    // instead of flashing "Redirecting…" and back. Offline, the retry is paused rather than
    // fetching, and the panel says it resumes when the connection returns.
    const [lastError, setLastError] = useState(error);
    if (error && error !== lastError) {
        setLastError(error);
    }
    const shownError = error ?? (isFetching || isPaused ? lastError : null);

    // A failed lookup used to leave "Redirecting…" on screen indefinitely. Show why, and how to
    // recover: an unknown key, a browser check (reload), or a retryable failure.
    const unknownKey = shownError?.data?.code === 'NOT_FOUND';

    return (
        <PageController route={Route}>
            <div className="flex flex-1 items-center justify-center">
                {shownError ? (
                    <InlineQueryError
                        error={shownError}
                        onRetry={() => void refetch()}
                        retrying={isFetching}
                        retryPaused={isPaused}
                        title={unknownKey ? 'This complaint link is not valid' : undefined}
                        description={
                            unknownKey
                                ? 'Check the link you were given, or contact the organisation you want to complain to.'
                                : undefined
                        }
                    />
                ) : (
                    <div className="text-lg text-muted-foreground">Redirecting to a new complaint form...</div>
                )}
            </div>
        </PageController>
    );
}
